<script lang="ts">
import { usePaymentProvider } from '@/plugins/usePaymentProvider'
import { InstantWinStorage } from '@/services/instant-win-storage'
import type { Nullable, Product, User } from '@/types'
import {
  PaymentProviders,
  PaymentType,
  type TPaymentTypeValue,
  type TProductSku,
} from '@/utils/helper-objects'
import {
  isDeposit,
  isInstantWinDraw,
  isPlan,
  isPurchasableProduct,
} from '@/utils/type-guard-utils'
import Payment, {
  type ApplePayButtonParams,
  type ClientSdkInstance,
  type GooglePayButtonParams,
  MessageType,
  Resign,
  type ResignFormConfig,
  type ResignRequest,
  type SolidgateFormInstance,
  type SolidgatePaymentIntentResponse,
  type SolidgatePaymentIntentUserData,
  type SolidgateResignIntentResponse,
} from '@solidgate/vue-sdk'
import { type PropType, defineComponent, isRef } from 'vue'

export default defineComponent({
  name: 'PaymentStageSolidgate',
  components: {
    Resign,
    Payment,
  },
  props: {
    /**
     * @model
     */
    modelValue: {
      type: Object as PropType<
        Pick<User, 'consent' | 'contact_optin'> & {
          [key: string]: any
        }
      >,
      required: true,
    },
    errors: {
      type: String as PropType<string | null>,
      default: null,
    },
    paymentErrors: {
      type: String,
      default: null,
    },
    showErrors: {
      type: Boolean,
      default: true,
    },
    product: {
      type: Object as PropType<Nullable<Product>>,
      default: null,
      validator(value: Nullable<Product>) {
        if (value === null) {
          return true
        }
        return isPurchasableProduct(value)
      },
    },
    ticketCount: {
      type: Number,
      required: false,
      default: null,
    },
    usedCredit: {
      type: Number,
      required: false,
      default: 0,
    },
    formValid: {
      type: Boolean,
      default: false,
    },
    formType: {
      type: String as PropType<'add-new-card' | 'one-click-pay'>,
      default: 'add-new-card',
    },
    isPaymentUpdate: {
      type: Boolean,
      default: false,
    },
    updateToken: {
      type: String,
      default: null,
    },
    email: {
      type: String,
      default: null,
    },
    apmConfig: {
      type: Object as PropType<{
        // `HTMLDivElement` should be available via DOM libs, but some tooling configs
        // (e.g. TS plugins running with a non-DOM lib) can flag it as missing.
        // We only need this as an opaque container reference for the SDK, so keep it loose.
        applePay?: Partial<ApplePayButtonParams & { element: unknown }>
        googlePay?: Partial<GooglePayButtonParams & { element: unknown }>
      }>,
      required: false,
    },

    autoUpdateIntent: {
      type: Boolean,
      default: true,
    },

    canOneClick: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'payment-authorized',
    'payment-error',
    'update:modelValue',
    'payment-processing',
    'payment-intent:updated',
    'customer-data:updated',
    'apm-enabled',
  ],

  data() {
    // Promise-based form instance management
    let formResolve: (form: SolidgateFormInstance) => void = () => {}
    const formPromise = new Promise<SolidgateFormInstance>((resolve) => {
      formResolve = resolve
    })

    // Get payment provider
    const paymentProvider = usePaymentProvider(PaymentProviders.Solidgate)
    const solidgateProvider = paymentProvider.activeProvider.value
    const { handlePaymentAuthorized } = paymentProvider
    const {
      createPaymentIntent,
      updatePaymentIntent,
      errors: solidgateErrors,
      createTokenUpdateIntent,
      createOneClickPayment,
      subscribeToOrderBroadcast,
      cleanupOrderSubscription,
    } = solidgateProvider

    // Set initial Apple Pay button state from prop
    const applePayButtonParams: ApplePayButtonParams = {
      enabled: false, // Start disabled, will be enabled when form is valid
      type: 'buy',
      integrationType: 'js',
      color: 'black',
      ...(this.apmConfig?.applePay ?? {}),
    }
    const googlePayButtonParams: GooglePayButtonParams = {
      enabled: false, // Start disabled, will be enabled when form is valid
      type: 'buy',
      color: 'black',
      ...(this.apmConfig?.googlePay ?? {}),
    }

    // Form parameters for Solidgate
    const solidgateFormParams = {
      allowSubmit: false,
    }

    const solidgateStyles = {
      'resign-form': {
        padding: '.5em 0px',
      },
      'resign-input-block': {
        input: {
          'background-color': '#fff',
          'box-shadow': 'none',
          padding: '1.75rem 1.5rem',
          'border-radius': '0.5rem',
        },
        '.label': {
          'font-weight': '700',
          'font-size': '0.875rem',
          'line-height': '1.25rem',
        },
      },
      'resign-input-group': {
        'margin-top': '0',
      },
      form_body: {
        'font-family': 'ui-sans-serif, system-ui, sans-serif',
      },
      'resign-card-preview': {
        display: 'none',
      },
      'resign-tooltip-icon': {
        display: 'none',
      },
      'payment-container': {
        'padding-top': '0',
        'padding-bottom': '0',
      },
      'form-container': {
        padding: '0',
      },
      card_number: {
        input: {
          'background-color': '#fff',
        },
        '.label': {
          'font-weight': '700',
          'font-size': '0.875rem',
          'line-height': '1.25rem',
        },
        i: {
          top: '2.8em !important',
        },
      },
      card_cvv: {
        input: {
          'background-color': '#fff',
        },
        '.label': {
          'font-weight': '700',
          'font-size': '0.875rem',
          'line-height': '1.25rem',
        },
        '.tooltip-icon': {
          display: 'none',
        },
      },
      expiry_date: {
        input: {
          'background-color': '#fff',
        },
        '.label': {
          'font-weight': '700',
          'font-size': '0.875rem',
          'line-height': '1.25rem',
        },
      },
    }

    const oneClickContainer: ResignFormConfig['container'] = {
      width: '100%',
    }

    const oneClickAppearance: ResignFormConfig['appearance'] = {
      submitButtonText: 'Pay',
      allowSubmit: false,
      resignCvvLabel: 'CVV',
      resignCvvPlaceholder: 'CVV',
      hideCvvNumbers: true,
    }

    return {
      // Payment provider properties
      paymentProvider,
      solidgateProvider,
      createPaymentIntent,
      updatePaymentIntent,
      createTokenUpdateIntent,
      subscribeToOrderBroadcast,
      cleanupOrderSubscription,
      handlePaymentAuthorized,
      createOneClickPayment,
      solidgateErrors,
      // Component state
      localErrors: null as string | null,
      errorDismissed: false,
      isProcessing: false,
      isUpdating: false,
      paymentIntentData: null as Nullable<SolidgatePaymentIntentResponse>,
      resignIntentData: null as Nullable<SolidgateResignIntentResponse>,
      resignFormSdk: null as Nullable<SolidgateFormInstance>,
      formMounted: false,
      initializationAttempts: 0,
      isCVVValid: false,
      formResolve,
      formPromise,

      // Apple/Google Pay button parameters
      applePayButtonParams,
      googlePayButtonParams,
      // Solidgate form parameters
      solidgateFormParams,
      // Solidgate styles
      solidgateStyles,

      // One-click form parameters
      oneClickAppearance,
      oneClickContainer,
    }
  },

  computed: {
    solidgateErrorMessage(): string | null {
      // `solidgateErrors` comes from the provider and is a `ref<string | null>`.
      // In Options API, refs are not auto-unwrapped in script, so we must unwrap here.
      if (isRef(this.solidgateErrors)) {
        const value = this.solidgateErrors.value
        return typeof value === 'string' ? value : null
      }

      return typeof this.solidgateErrors === 'string'
        ? this.solidgateErrors
        : null
    },

    // Combine error sources
    displayErrors(): string | null {
      // If user dismissed the error, don't show it
      if (this.errorDismissed || !this.showErrors) {
        return null
      }

      // IMPORTANT: keep this computed PURE (no side-effects / emits).
      // Emitting here can cause parent handlers to set a generic fallback error
      // (e.g. when message is null), which results in phantom "Payment processing failed"
      // banners on pages like /payment-recovery.
      return (
        this.errors ||
        this.localErrors ||
        this.solidgateErrorMessage ||
        this.paymentErrors
      )
    },

    showAddCardForm(): boolean {
      return this.formType !== 'one-click-pay'
    },

    showOneClickPayForm(): boolean {
      return this.formType === 'one-click-pay'
    },

    // Extract merchant data for Solidgate component
    merchantData(): Nullable<SolidgatePaymentIntentResponse['merchantData']> {
      if (!this.paymentIntentData) {
        return null
      }

      // Validate that the merchantData object has the required fields
      const md = this.paymentIntentData.merchantData
      if (!md || !md.merchant || !md.signature || !md.paymentIntent) {
        console.warn(
          'Payment intent data is missing required merchant data fields'
        )
        return null
      }

      return {
        merchant: md.merchant,
        signature: md.signature,
        paymentIntent: md.paymentIntent,
      }
    },

    resignMerchantData(): Nullable<ResignRequest> {
      if (!this.resignIntentData) {
        return null
      }

      const md = this.resignIntentData?.csi_content?.merchantData
      if (!md.paymentIntent || !md.merchant || !md.signature) {
        return null
      }

      return {
        merchant: md.merchant,
        signature: md.signature,
        resignIntent: md.paymentIntent,
      }
    },

    // Two-way binding for form data
    form: {
      get() {
        return this.modelValue
      },
      set(value: any) {
        this.$emit('update:modelValue', value)
      },
    },

    // Coupon code for plans that need a predefined discount reapplied after updates
    couponCode(): string | null {
      return isPlan(this.product)
        ? this.product.coupon_code?.trim() || null
        : null
    },
  },

  watch: {
    // Reset dismissed flag when new errors appear
    errors() {
      this.errorDismissed = false
    },
    localErrors(newError: string | null, oldError: string | null) {
      this.errorDismissed = false
      if (newError && newError !== oldError) {
        this.$emit('payment-error', { message: newError })
      }
    },
    solidgateErrorMessage(newError: string | null, oldError: string | null) {
      this.errorDismissed = false
      if (newError && newError !== oldError) {
        this.$emit('payment-error', { message: newError })
      }
    },
    async showOneClickPayForm() {
      this.cleanupOrderSubscription()
      if (this.showOneClickPayForm) {
        await this.reloadOneClickPaymentIntent()
      } else {
        this.resignIntentData = null
        this.subscribeToOrderBroadcast(this.paymentIntentData?.order_id)
        await this.$nextTick()
        this.clearErrors()
      }
    },
  },

  async mounted() {
    await this.initializePayment()
  },

  beforeUnmount() {
    // Clean up any active broadcast subscriptions
    this.cleanupOrderSubscription()
  },

  methods: {
    async reloadOneClickPaymentIntent() {
      try {
        const userData = this.prepareUserData()
        const oneClickIntent = await this.createOneClickPayment(userData)
        this.resignIntentData = oneClickIntent
        this.subscribeToOrderBroadcast(oneClickIntent.csi_content.order_id)
      } catch (e: any) {
        this.handleError(e, 'Failed to create one-click payment')
      }
    },
    /**
     * Calculate amount based on product type and quantity
     */
    calculateAmount(product: Nullable<Product>, count: number = 1): number {
      if (!product) {
        return 0
      }

      if (isPlan(product)) {
        return product.has_free_trial !== true ? product.discount_cost * 100 : 0
      }

      if (isInstantWinDraw(product)) {
        return product.ticket_cost * count * 100
      }

      if (isDeposit(product)) {
        return product.amount * 100
      }

      return 0
    },

    /**
     * Get payment/product type
     */
    getPaymentType(product: unknown): TPaymentTypeValue {
      // For payment updates, always return PaymentUpdate type
      if (this.isPaymentUpdate) {
        return PaymentType.PaymentUpdate
      }

      if (isPlan(product)) {
        return PaymentType.Subscription
      } else if (isInstantWinDraw(product)) {
        return PaymentType.InstantWin
      } else if (isDeposit(product)) {
        return PaymentType.Product
      }

      throw new Error('Unsupported product type for payment')
    },

    /**
     * When the form is mounted
     */
    handleMounted(): void {
      this.formMounted = true
    },

    /**
     * Handle form submission event from Solidgate
     */
    handleFormSubmit(_event: any): void {
      // Set processing state when form is submitted
      this.$emit('payment-processing', true)
      this.isProcessing = true
    },

    /**
     * Store form instance when Solidgate is ready
     */
    async onReadyPaymentInstance(form: unknown): Promise<void> {
      this.formResolve(form as SolidgateFormInstance)

      // Re-apply coupon on initial mount for plans that rely on a predefined coupon
      await this.applyCouponIfNeeded(form as SolidgateFormInstance)
    },

    onResignReady(form: unknown) {
      this.resignFormSdk = form as SolidgateFormInstance
    },
    async onResignFailed(error: any) {
      if (!this.showOneClickPayForm) {
        return
      }

      await this.handleError(
        error,
        'CVV error, please try again',
        !this.showOneClickPayForm
      )
    },
    onResignInteraction(event: any) {
      if (!this.showOneClickPayForm) {
        return
      }

      const resignCvv = event.resignForm?.fields?.resignCvv
      this.isCVVValid = (resignCvv?.isTouched && resignCvv?.isValid) || false
    },

    /**
     * Centralized error handler
     */
    async handleError(
      error: any,
      defaultMessage: string,
      suppressError: boolean = false
    ): Promise<any> {
      const errorMessage =
        error instanceof Error ? error.message : defaultMessage
      if (!suppressError) {
        this.localErrors = errorMessage
      }

      // Reset processing state for UI
      this.$emit('payment-processing', false)
      this.isProcessing = false

      // Clean up any active broadcast subscriptions
      this.cleanupOrderSubscription()

      await this.$nextTick()

      // Re-init relevant payment intent
      if (!this.showOneClickPayForm) {
        // Reset the form state
        this.formMounted = false
        this.paymentIntentData = null
        await this.initializePayment()
      } else {
        await this.reloadOneClickPaymentIntent()
      }

      if (!suppressError) {
        this.$emit('payment-error', { message: errorMessage })
      }

      // `payment-error` is emitted by the `localErrors` watcher (only on real changes).
      return { refreshed: false, success: false, error }
    },

    /**
     * Reapply a predefined coupon to the Solidgate form instance.
     * Required because Solidgate drops predefined coupons after form updates.
     */
    async applyCouponIfNeeded(
      formInstance: SolidgateFormInstance
    ): Promise<void> {
      const coupon = this.couponCode
      if (!coupon) {
        return
      }

      const formWithCoupon = formInstance as SolidgateFormInstance & {
        applyCoupon?: (couponCode: string) => Promise<unknown>
      }

      if (typeof formWithCoupon.applyCoupon !== 'function') {
        console.warn('Solidgate form instance does not support applyCoupon')
        return
      }

      try {
        await formWithCoupon.applyCoupon(coupon)
      } catch (error) {
        console.warn('Failed to apply coupon to Solidgate form', error)
      }
    },

    /**
     * Initialize payment intent when component mounts
     */
    async initializePayment(): Promise<void> {
      // Increment the initialization attempt counter
      this.initializationAttempts++

      // Check if we've exceeded the maximum number of attempts (5 attempts)
      if (this.initializationAttempts > 5) {
        this.localErrors =
          'Failed to initialize payment form after multiple attempts. Please reload the page.'
        this.$emit('payment-processing', false)
        return // Stop further execution
      }

      try {
        // Check if this is a payment update mode
        if (this.isPaymentUpdate && this.updateToken) {
          // For payment updates, use the createTokenUpdateIntent method
          const userData = this.prepareUserData()
          const data = await this.createTokenUpdateIntent(
            0,
            userData.email,
            userData
          )

          // Store the email used to identify when an update is needed
          data.email = userData.email

          // Ensure we are listening for status updates as soon as the intent exists
          if (data.order_id) {
            this.subscribeToOrderBroadcast(data.order_id)
          }
          this.paymentIntentData = data

          // Emit customer data if available
          if (data.customer) {
            this.$emit('customer-data:updated', data.customer)
          }

          // Emit event to notify parent component
          this.$emit('payment-intent:updated', this.paymentIntentData)
        } else {
          // Regular payment flow
          // Use prepareUserData for consistent data preparation
          const userData = this.prepareUserData()

          // Create payment intent with all the data
          const data = await this.createPaymentIntent(
            this.usedCredit,
            userData.email,
            userData
          )

          // Store the email used to identify when an update is needed
          data.email = userData.email

          // Subscribe to broadcast (need at this point due to Apple/Google Pay)
          if (data.order_id) {
            this.subscribeToOrderBroadcast(data.order_id)
          }
          this.paymentIntentData = data

          // Emit event to notify parent component
          this.$emit('payment-intent:updated', this.paymentIntentData)
        }
      } catch (e) {
        console.error('Payment initialization error:', e)

        // For payment updates, emit error to parent instead of retrying
        if (this.isPaymentUpdate || this.canOneClick) {
          this.localErrors =
            "You don't have active subscriptions or there's an issue with the payment update form."
          return
        }

        return await this.handleError(e, 'Failed to create payment intent')
      }
    },

    /**
     * Prepare user data with all necessary fields
     */
    prepareUserData(orderId?: string): SolidgatePaymentIntentUserData {
      const isInstantWin = this.product && isInstantWinDraw(this.product)
      const isPlanProduct = this.product && isPlan(this.product)
      const isDepositProduct = this.product && isDeposit(this.product)

      // Use the user's email if available, otherwise use a temporary one
      const userEmail =
        this.email && this.email.trim() !== ''
          ? this.email
          : this.modelValue.email && this.modelValue.email.trim() !== ''
            ? this.modelValue.email
            : 'guest-customer@goodlifeplus.co.uk'

      const userData: SolidgatePaymentIntentUserData = {
        email: userEmail,
        ...this.modelValue,
        paymentType: this.getPaymentType(this.product),
        product_sku: isDepositProduct ? this.product.sku : undefined,
        product_amount: isDepositProduct ? this.product.amount : undefined,
        order_description: '',
        amount: 0,
      }

      // Set appropriate order description based on product type
      if (isInstantWin) {
        userData.order_description = `Instant win ${this.ticketCount}x ${this.product?.code}`
      } else if (isPlanProduct) {
        userData.order_description = `Subscription ${this.product.name} (${this.product.period})`
      } else if (isDepositProduct) {
        userData.order_description = `Deposit top-up ${this.product.amount}`
      } else if (this.isPaymentUpdate) {
        userData.order_description = 'Payment method update'
        userData.token = this.updateToken
      }

      if (orderId) {
        userData.order_id = orderId
      }

      // Data mapping for different field names
      userData.line1 = this.modelValue.address_line_1
      userData.line2 = this.modelValue.address_line_2
      userData.postcode = this.modelValue.post_code

      // Add product-specific data
      if (isPlanProduct) {
        // Note: Technically plan codes are not in the list of product SKUs,
        // but for consistency across other product types this is cast to TProductSku
        userData.product_code = this.product.code as TProductSku
        userData.plan_code = this.product.code
        userData.amount =
          this.product.has_free_trial !== true
            ? Math.round(this.product.discount_cost * 100)
            : 0
      } else if (isInstantWin) {
        userData.instant_win_code = this.product.code
        userData.instant_win_price = this.product.ticket_cost
        userData.quantity = this.ticketCount

        // Send amount in cents with floating point protection
        userData.amount = Math.round(
          this.product.ticket_cost * this.ticketCount * 100
        )

        // Add reservation data from sessionStorage
        try {
          const reservationData = InstantWinStorage.getStoredReservation(
            this.product.code
          )
          if (reservationData?.lock_key) {
            userData.instant_win_lock_key = reservationData.lock_key
          }
        } catch (e) {
          console.warn('Could not retrieve reservation data', e)
        }
      } else if (isDepositProduct) {
        userData.amount = this.product.amount
      } else {
        userData.amount = 0
      }

      // Default values for analytics fields
      userData.utm_source = ''
      userData.utm_medium = ''
      userData.utm_campaign = ''

      return userData
    },

    /**
     * Handle form validation state changes for alternative payment methods
     */
    updateAPMButtonState(isValid: boolean): void {
      // Only enable APMs for non-subscriptions purchases
      const isApmAvailable = this.product && !isPlan(this.product)

      // Update the Apple and Google Pay button state
      // APMs are always disabled for subscriptions, only enabled when form is valid
      this.applePayButtonParams.enabled = Boolean(isValid && isApmAvailable)
      this.googlePayButtonParams.enabled = Boolean(isValid && isApmAvailable)

      // Skip payment intent updates for payment recovery - only update for regular checkout
      if (this.isPaymentUpdate) {
        return
      }

      // If valid, also update payment intent - same logic as in the watcher
      if (isValid === true && this.autoUpdateIntent) {
        void this.$nextTick(async () => {
          // Only proceed if we have payment intent data and form is mounted
          if (
            this.paymentIntentData?.merchantData?.paymentIntent &&
            this.formMounted &&
            !this.isProcessing
          ) {
            try {
              // Get form instance
              const formInstance = await this.formPromise

              // Prepare user data
              const userData = this.prepareUserData(
                this.paymentIntentData.order_id
              )

              // Update payment intent with current form data
              await this.updatePaymentIntentWithFormData(
                this.paymentIntentData.merchantData.paymentIntent,
                this.modelValue.email,
                userData,
                this.usedCredit,
                formInstance
              )

              this.$emit('apm-enabled', {
                applePay: this.applePayButtonParams.enabled,
                googlePay: this.googlePayButtonParams.enabled,
              })
            } catch (error) {
              console.warn(
                'Failed to update payment intent via direct method call:',
                error
              )
            }
          }
        })
      }
    },

    /**
     * Handle payment result from Solidgate component
     */
    async handlePaymentResult(event: any) {
      if (event.type === 'success') {
        // Make sure processing state is active for success path
        this.$emit('payment-processing', true)
        this.isProcessing = true

        // Reset the counter when successful
        this.initializationAttempts = 0

        try {
          // Extract payment details
          const orderId = event.order.order_id
          const token = event.token

          // Prepare user data for payment processing
          const userData = this.prepareUserData(orderId)

          // Process the successful payment through the central payment provider
          const handleResult = await this.handlePaymentAuthorized(
            { token },
            userData
          )

          if (handleResult.success) {
            // Emit event to navigate to confirmation page
            // Keep processing state active as we're about to redirect
            this.$emit('payment-authorized', {
              success: true,
              data: handleResult.data || {},
              orderStatus: handleResult.orderStatus,
              redirect: handleResult.redirect,
              orderId,
            })
          } else {
            // Payment was not successful
            const errorMessage =
              handleResult.data?.error || 'Failed to process payment'

            return await this.handleError(
              new Error(errorMessage),
              'Payment processing failed'
            )
          }
        } catch (error) {
          return await this.handleError(error, 'Payment processing failed')
        }
      } else if (event.type === 'error' || event.type === 'fail') {
        return await this.handleError(
          new Error(event.message),
          'Payment failed'
        )
      }
    },

    /**
     * Process payment - called by parent component
     */
    async processPayment(): Promise<any> {
      // Clear any previous errors
      this.localErrors = null

      // Validate prerequisites
      if (!this.paymentIntentData) {
        this.localErrors = 'Payment system not properly initialized'
        return { success: false, error: this.localErrors }
      }

      if (!this.modelValue.email || this.modelValue.email.trim() === '') {
        this.localErrors = 'Please provide a valid email address'
        return { success: false, error: this.localErrors }
      }

      // Check if the form is mounted - form should be ready before submission
      if (!this.formMounted) {
        this.localErrors = 'Payment form is not ready yet. Please try again.'
        return { success: false, error: this.localErrors }
      }

      try {
        // Get the order ID from payment intent data
        const orderId = this.paymentIntentData.order_id

        // Validate the order ID exists
        if (!orderId) {
          this.localErrors = 'Missing order ID in payment data'
          return { success: false, error: this.localErrors }
        }

        // Subscribe to broadcast (as safety net)
        this.subscribeToOrderBroadcast(orderId)

        const userData = this.prepareUserData(orderId)

        // Get form instance
        const formInstance = await this.formPromise

        try {
          // Validate that we have the payment intent before updating
          const paymentIntent =
            this.paymentIntentData.merchantData?.paymentIntent
          if (!paymentIntent) {
            throw new Error('Missing payment intent required for update')
          }

          const result = await this.updatePaymentIntentWithFormData(
            paymentIntent,
            this.modelValue.email,
            userData,
            this.usedCredit,
            formInstance
          )

          if (!result.success) {
            throw new Error(
              result.error || 'Failed to update payment information'
            )
          }
        } catch (error) {
          return await this.handleError(
            error,
            'Failed to update payment information'
          )
        }

        // Add a small delay before submitting the form for Safari compatibility
        await new Promise((resolve) => setTimeout(resolve, 100))

        try {
          // Attempt to submit the form - the processing state will be set in the handleFormSubmit method
          const submitResult = (await formInstance.submit()) as any

          if (submitResult && submitResult.error) {
            throw submitResult
          }
        } catch (submitError: any) {
          console.error('Solidgate submit error:', submitError)
          // Solidgate returns validation errors as rejected promises
          this.$emit('payment-processing', false)
          this.isProcessing = false

          const validationMessage =
            submitError?.message ||
            submitError?.error?.message ||
            submitError?.data?.message ||
            'Please check your card details.'

          if (
            submitError?.code === 'validation_error' ||
            submitError?.error?.code === 'validation_error' ||
            submitError?.data?.code === 'validation_error'
          ) {
            this.localErrors = validationMessage
            return { success: false, error: this.localErrors }
          }

          // Non-validation errors: surface message and stop processing without rethrowing
          this.localErrors = validationMessage
          return { success: false, error: this.localErrors }
        }

        // Safety timeout: if no event occurs within 8 seconds, reset processing state
        setTimeout(() => {
          if (this.isProcessing) {
            console.warn(
              'Solidgate form submission timed out (no event received)'
            )
            this.isProcessing = false
          }
        }, 8000)

        return { submitted: true }
      } catch (error) {
        this.localErrors =
          error instanceof Error ? error.message : 'Payment processing failed'
        return { success: false, error: this.localErrors }
      }
    },

    async processOneClickPayment(): Promise<any> {
      // Clear any previous errors
      this.localErrors = null

      if (!this.resignFormSdk) {
        console.error('form instance not set')
        return { success: false, error: 'Form instance not set' }
      }

      if (!this.isCVVValid) {
        return { success: false, error: 'CVV is not valid' }
      }

      try {
        this.$emit('payment-processing', true)
        this.isCVVValid = false
        this.isProcessing = true
        await this.resignFormSdk.submit()
      } catch (submitError: any) {
        return await this.handleError(
          submitError,
          'CVV error, please try again'
        )
      }

      return { success: true }
    },
    /**
     * Common function to update payment intent and form data
     */
    async updatePaymentIntentWithFormData(
      paymentIntent: string,
      email: string,
      userData: SolidgatePaymentIntentUserData,
      creditAmount: number,
      formInstance: SolidgateFormInstance
    ): Promise<{ success: boolean; error?: string }> {
      try {
        // Update payment intent
        const updatedIntent = await this.updatePaymentIntent(
          paymentIntent,
          email,
          userData,
          creditAmount
        )

        // Handle different response structures
        if (!updatedIntent.merchantData && updatedIntent.updateData) {
          updatedIntent.merchantData = {
            partialIntent: updatedIntent.updateData.partialIntent,
            signature: updatedIntent.updateData.signature,
          }
        }

        // Validate the response structure
        if (
          !updatedIntent.merchantData ||
          !updatedIntent.merchantData.partialIntent ||
          !updatedIntent.merchantData.signature
        ) {
          throw new Error(
            'Invalid payment data - missing required fields for form update'
          )
        }

        // Update form with new data
        await formInstance.update({
          partialIntent: updatedIntent.merchantData.partialIntent,
          signature: updatedIntent.merchantData.signature,
        })

        // Re-apply predefined coupon after intent updates (Solidgate drops them otherwise)
        await this.applyCouponIfNeeded(formInstance)

        // Update stored data, preserving the original merchantData structure
        this.paymentIntentData = {
          ...(this.paymentIntentData ?? {}),
          email: email,
          // Only update the specific properties we need for the form update
          merchantData: {
            ...this.paymentIntentData!.merchantData,
            partialIntent: updatedIntent.merchantData.partialIntent,
            signature: updatedIntent.merchantData.signature,
          },
          user_progression: updatedIntent.user_progression,
        }

        // Emit event to notify parent component
        this.$emit('payment-intent:updated', this.paymentIntentData)

        return { success: true }
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to update payment information',
        }
      }
    },

    /**
     * Update payment intent with new credit amount
     * Called externally when user toggles credit usage
     */
    async updateCreditAmount(creditAmount: number) {
      // Ensure we have payment intent data available
      if (!this.paymentIntentData?.merchantData?.paymentIntent) {
        console.warn(
          'Cannot update payment intent - missing payment intent data'
        )
        return { success: false, error: 'Missing payment intent data' }
      }

      if (this.isProcessing) {
        console.warn('Cannot update payment intent while processing')
        return { success: false, error: 'Payment is currently processing' }
      }

      try {
        // Show overlay to indicate payment is updating
        this.isUpdating = true
        // Get form instance
        const formInstance = await this.formPromise

        // Prepare user data
        const userData = this.prepareUserData(this.paymentIntentData.order_id)

        // Use the common function to update payment intent and form
        const result = await this.updatePaymentIntentWithFormData(
          this.paymentIntentData.merchantData.paymentIntent,
          this.modelValue.email,
          userData,
          creditAmount,
          formInstance
        )

        // Payment is no longer updating
        this.isUpdating = false

        if (!result.success) {
          this.localErrors = result.error || null
          return { success: false, error: this.localErrors }
        }

        return { success: true }
      } catch (error) {
        this.localErrors =
          error instanceof Error
            ? error.message
            : 'Failed to update payment information'

        // Make sure overlay is removed on error
        this.isUpdating = false

        return { success: false, error: this.localErrors }
      }
    },

    /**
     * Clear errors manually
     */
    clearErrors() {
      this.localErrors = null
      this.errorDismissed = true
    },
  },
})
</script>

<template>
  <fieldset>
    <!-- Error banner -->
    <div
      v-if="displayErrors"
      class="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 p-4"
    >
      <div class="flex items-start justify-between">
        <div class="flex items-start">
          <AppIcon
            :icon="Icons.Error"
            class="mr-3 mt-1 text-red-500"
            size="sm"
          />
          <div>
            <h3 class="text-sm font-medium text-red-800">Payment Error</h3>
            <p class="mt-1 text-sm text-red-700">
              {{ displayErrors }}
            </p>
          </div>
        </div>
        <button
          class="ml-4 inline-flex rounded-md bg-red-50 p-1.5 text-red-400 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
          @click="clearErrors"
        >
          <AppIcon :icon="Icons.Times" size="xs" />
        </button>
      </div>
    </div>

    <!-- Solidgate Payment Component -->
    <div v-if="merchantData && showAddCardForm" class="payment-container">
      <Payment
        v-show="showAddCardForm"
        ref="cardComponentRef"
        :merchant-data="merchantData"
        :form-params="solidgateFormParams"
        :styles="solidgateStyles"
        :apple-pay-button-params="applePayButtonParams"
        :apple-pay-container-ref="apmConfig?.applePay?.element as any"
        :google-pay-button-params="googlePayButtonParams"
        :google-pay-container-ref="apmConfig?.googlePay?.element as any"
        width="100%"
        @success="handlePaymentResult"
        @error="handlePaymentResult"
        @fail="handlePaymentResult"
        @submit="handleFormSubmit"
        @mounted="handleMounted"
        @ready-payment-instance="onReadyPaymentInstance"
      />
      <!-- Overlay that appears during updates -->
      <div v-if="isUpdating" class="payment-overlay">
        <div class="update-message">
          <span>Updating payment information...</span>
        </div>
      </div>
    </div>

    <div v-if="resignMerchantData" v-show="showOneClickPayForm">
      <div class="flex items-center gap-2">
        <p class="font-bold flex basis-4/6">
          Please enter your CVV, card details saved
        </p>
        <div class="flex basis-2/6">
          <Resign
            :key="resignMerchantData?.signature"
            :resign-request="resignMerchantData"
            :appearance="oneClickAppearance"
            :container="oneClickContainer"
            :styles="solidgateStyles"
            width="100%"
            @ready-resign-instance="onResignReady"
            @resign-init-failed="onResignFailed"
            @success="handlePaymentResult"
            @error="onResignFailed"
            @fail="onResignFailed"
            @interaction="onResignInteraction"
          />
        </div>
      </div>
    </div>

    <div v-if="!merchantData && !resignMerchantData" class="p-4 text-center">
      <span class="text-gray-500">Initializing payment form...</span>
    </div>

    <slot name="optin">
      <div v-if="product" class="mt-6">
        <AppCheckbox
          v-model="form.consent"
          name="consent"
          class="my-3"
          container-class="text-sm"
          :label="$t('form.checkbox.consent_accept')"
          :errors="displayErrors"
          :required="true"
        />

        <AppCheckbox
          v-model="form.contact_optin"
          name="contact_optin"
          container-class="text-sm"
          :label="$t('form.checkbox.marketing_comms_accept')"
          :errors="displayErrors"
        />
      </div>
    </slot>
  </fieldset>
</template>

<style lang="scss">
/* Payment container positioning */
.payment-container {
  position: relative;
}

/* Overlay during payment updates */
.payment-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}

/* Update message styling */
.update-message {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 0.875rem;
  color: #4b5563;
}
</style>
